import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import {
  enumFreightTerm,
  enumPaymentTerm,
  FreightTerm,
  PaymentTerm,
} from '@rematter/frontend-lib-api-client';
import {
  ContentSection,
  DatePicker,
  Label,
  Select,
  TextArea,
  TextField,
  type DateValue,
} from '@rematter/ui-web';
import { useAtom } from 'jotai';

import { formatAddressDisplay } from './helpers/formatAddress';
import { useSupplierAccount } from './hooks/useSupplierAccount';

import {
  $purchaseOrderForm,
  $updatePurchaseOrderForm,
} from '~/modules/trade/_state/purchaseOrderForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectContact } from '~/shared/components/select-fields/SelectContact';
import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectSupplierAccount } from '~/shared/components/select-fields/SelectSupplierAccount';
import { SelectUser } from '~/shared/components/select-fields/SelectUser';

export type Props = {
  disabled?: boolean;
};

export const PurchaseOrderWizardStepContractInfo: React.FC<Props> = ({
  disabled = false,
}) => {
  const t = useTranslations();

  const [form] = useAtom($purchaseOrderForm);
  const [, updateForm] = useAtom($updatePurchaseOrderForm);

  const { selectedAccount } = useSupplierAccount(form.supplierId);

  const hasSelectedSupplier = !!form.supplierId;

  const selectedAccountAddresses = React.useMemo(
    () => selectedAccount?.addresses ?? [],
    [selectedAccount],
  );

  const addressOptions = React.useMemo(
    () =>
      selectedAccountAddresses?.map((address) => ({
        key: address.id,
        label: `${address.name ?? address.address.addressType} - ${formatAddressDisplay(address.address)}`,
      })) ?? [],
    [selectedAccountAddresses],
  );

  const handleFacilityChange = React.useCallback(
    (facilityId: string | undefined) => {
      updateForm({ facilityId: facilityId ?? null });
    },
    [updateForm],
  );

  const handleStartDateChange = React.useCallback(
    (date: DateValue | null) => {
      updateForm({ purchaseOrderStartDate: date as CalendarDate | null });
    },
    [updateForm],
  );

  const handleEndDateChange = React.useCallback(
    (date: DateValue | null) => {
      updateForm({ purchaseOrderEndDate: date as CalendarDate | null });
    },
    [updateForm],
  );

  const handleFieldChange = React.useCallback(
    <K extends keyof typeof form>(field: K) =>
      (value: (typeof form)[K]) => {
        updateForm({ [field]: value });
      },
    [updateForm],
  );

  const handleSelectChange = React.useCallback(
    (field: string, value: string | null | undefined) => {
      updateForm({ [field]: value });
    },
    [updateForm],
  );

  const handleSupplierChange = React.useCallback(
    (value: string | null | undefined) => {
      const supplierId = value ?? null;
      updateForm({
        supplierId,
        contactId: null,
        supplierAddressId: null,
      });
    },
    [updateForm],
  );

  const handleAddressChange = React.useCallback(
    (addressId: string) => {
      updateForm({
        supplierAddressId: addressId as string,
      });
    },
    [updateForm],
  );

  return (
    <div className="@PurchaseOrderWizardStepContractInfo bg-white">
      <ContentSection
        variant="panel"
        title={t.purchaseOrdersView.wizard.contractInfo.contractInfo}
      >
        <div className="grid grid-cols-4 items-start gap-4">
          <TextField
            label={t.purchaseOrdersView.wizard.contractInfo.purchaseOrderNumber}
            value={form.purchaseOrderNumber}
            onChange={handleFieldChange('purchaseOrderNumber')}
            placeholder={t.common.placeholders.generateNumber}
            isRequired
            isDisabled={disabled}
          />
          <SelectFacility
            value={form.facilityId}
            onChange={handleFacilityChange}
            label={t.purchaseOrdersView.wizard.contractInfo.facility}
            autoSelectFirstOption
            isRequired
            isDisabled={disabled}
          />

          <DatePicker
            value={form.purchaseOrderStartDate as DateValue | null}
            onChange={handleStartDateChange}
            label={
              t.purchaseOrdersView.wizard.contractInfo.purchaseOrderStartDate
            }
            isRequired
            isDisabled={disabled}
          />
          <DatePicker
            value={form.purchaseOrderEndDate as DateValue | null}
            onChange={handleEndDateChange}
            label={
              t.purchaseOrdersView.wizard.contractInfo.purchaseOrderEndDate
            }
            isDisabled={disabled}
          />
          <SelectUser
            value={form.accountRepresentativeId ?? undefined}
            placeholder={
              t.purchaseOrdersView.wizard.placeholders.accountRepresentative
            }
            onChange={(value: string | undefined) =>
              handleSelectChange('accountRepresentativeId', value ?? null)
            }
            label={
              t.purchaseOrdersView.wizard.contractInfo.accountRepresentative
            }
            isDisabled={disabled}
          />
          <TextField
            value={form.counterpartyReferenceNumber}
            onChange={handleFieldChange('counterpartyReferenceNumber')}
            label={
              t.purchaseOrdersView.wizard.contractInfo
                .counterpartySalesOrderNumber
            }
            placeholder={
              t.purchaseOrdersView.wizard.placeholders
                .counterpartySalesOrderNumber
            }
            isDisabled={disabled}
          />
        </div>
      </ContentSection>

      <ContentSection
        variant="panel"
        title={t.purchaseOrdersView.wizard.contractInfo.supplierInformation}
      >
        <div className="grid grid-cols-3 gap-4">
          <SelectSupplierAccount
            value={form.supplierId}
            onChange={(value) => handleSupplierChange(value)}
            label={t.purchaseOrdersView.wizard.contractInfo.supplierName}
            isRequired
            placeholder={t.purchaseOrdersView.wizard.placeholders.supplier}
            isDisabled={disabled}
          />
          <SelectContact
            value={form.contactId}
            onChange={(value) => handleSelectChange('contactId', value)}
            label={t.purchaseOrdersView.wizard.contractInfo.contact}
            accountIds={form.supplierId ? [form.supplierId] : []}
            isDisabled={!hasSelectedSupplier || disabled}
          />
          <Select
            label={t.purchaseOrdersView.wizard.contractInfo.accountAddress}
            options={addressOptions}
            value={form.supplierAddressId}
            onChange={handleAddressChange}
            placeholder={
              hasSelectedSupplier
                ? t.purchaseOrdersView.wizard.placeholders.accountAddress
                : t.purchaseOrdersView.wizard.placeholders.selectSupplierFirst
            }
            isDisabled={!hasSelectedSupplier || disabled}
            isRequired
          />
        </div>
      </ContentSection>

      <ContentSection
        variant="panel"
        title={t.purchaseOrdersView.wizard.contractInfo.paymentInfo}
      >
        <div className="grid grid-cols-3 gap-4">
          <SelectCurrency
            value={form.paymentCurrency}
            onChange={(value) => handleSelectChange('paymentCurrency', value)}
            label={t.purchaseOrdersView.wizard.contractInfo.paymentCurrency}
            isRequired
            isDisabled={disabled}
          />
          <SelectEnum<PaymentTerm>
            enumValue={enumPaymentTerm}
            isRequired
            value={form.paymentTerm ?? undefined}
            onChange={(value) =>
              handleSelectChange('paymentTerm', value as string | null)
            }
            label={t.purchaseOrdersView.wizard.contractInfo.paymentTerm}
            isDisabled={disabled}
          />
          <SelectEnum<FreightTerm>
            enumValue={enumFreightTerm}
            isRequired
            value={form.freightTerm}
            onChange={(value) => handleSelectChange('freightTerm', value)}
            label={t.purchaseOrdersView.wizard.contractInfo.freightTerm}
            isDisabled={disabled}
          />
        </div>
      </ContentSection>

      <ContentSection
        variant="panel"
        title={t.purchaseOrdersView.wizard.contractInfo.otherInfo}
        hideDivider
      >
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label className="mb-1 block text-sm font-medium text-neutral/70">
              {t.purchaseOrdersView.wizard.contractInfo.notes}
            </Label>
            <TextArea
              value={form.notes}
              onChange={handleFieldChange('notes')}
              placeholder={
                t.purchaseOrdersView.wizard.contractInfo.notesPlaceholder
              }
              rows={3}
              isDisabled={disabled}
            />
          </div>
        </div>
      </ContentSection>
    </div>
  );
};
